import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { CurrencyInr, Spinner, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RZP_PUBLIC_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Load the Razorpay checkout script once and cache the promise so repeated
// clicks don't re-inject the same <script> tag.
let _scriptPromise = null;
const loadCheckoutScript = () => {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => reject(new Error('Razorpay load failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error('Razorpay load failed'));
    document.body.appendChild(s);
  });
  return _scriptPromise;
};

/**
 * RazorpayButton — opens the standard Razorpay checkout modal.
 *
 * Props:
 *  - amount: paise (integer) — REQUIRED when no `bookingId`
 *  - bookingId: optional package_bookings._id; if supplied the server uses the
 *    booking's total and reconciles `payment_status` on verify success
 *  - label, className, testId: cosmetic overrides
 *  - onSuccess(payload): called with `{order_id, payment_id, booking_id}` on
 *    verified payment
 *  - onFailure(err): called on dismiss / failure
 *  - disabled: bool
 */
const RazorpayButton = ({
  amount,
  bookingId,
  currency = 'INR',
  receipt,
  description = 'BeeWeddy Booking',
  label,
  className = '',
  testId = 'razorpay-pay-btn',
  onSuccess,
  onFailure,
  disabled = false,
  paid = false,
  customer, // { name, email, contact } — overrides backend-inferred values
  purpose = 'final_total', // 'final_total' | 'confirmation_fee'
}) => {
  const [loading, setLoading] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadCheckoutScript()
      .then(() => { if (!cancelled) setScriptReady(true); })
      .catch(() => { if (!cancelled) setScriptReady(false); });
    return () => { cancelled = true; };
  }, []);

  const handleClick = useCallback(async () => {
    if (paid || disabled || loading) return;
    if (!scriptReady || !window.Razorpay) {
      toast.error('Payment SDK is still loading. Please try again in a moment.');
      return;
    }
    setLoading(true);
    let orderPayload;
    try {
      const body = { currency, purpose };
      if (bookingId) body.booking_id = bookingId;
      else body.amount = amount;
      if (receipt) body.receipt = receipt;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/create-order`,
        body,
        { withCredentials: true }
      );
      orderPayload = data;
    } catch (e) {
      setLoading(false);
      const detail = e?.response?.data?.detail || 'Could not start payment';
      toast.error(detail);
      if (onFailure) onFailure(e);
      return;
    }

    const keyId = orderPayload.key_id || RZP_PUBLIC_KEY;
    if (!keyId) {
      setLoading(false);
      toast.error('Razorpay is not configured. Contact support.');
      return;
    }

    const options = {
      key: keyId,
      amount: orderPayload.amount,
      currency: orderPayload.currency || 'INR',
      name: 'BeeWeddy',
      description,
      order_id: orderPayload.order_id,
      prefill: {
        name: (customer && customer.name) || orderPayload.name || '',
        email: (customer && customer.email) || orderPayload.email || '',
        contact: (customer && customer.contact) || orderPayload.contact || '',
      },
      theme: { color: '#B85A48' },
      modal: {
        ondismiss: () => {
          setLoading(false);
          if (onFailure) onFailure({ reason: 'dismissed' });
        },
      },
      handler: async (resp) => {
        // Verify on the server BEFORE telling the user "paid".
        try {
          const { data } = await axios.post(
            `${BACKEND_URL}/api/payments/verify-payment`,
            {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            },
            { withCredentials: true }
          );
          toast.success('Payment successful');
          if (onSuccess) onSuccess(data);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Payment verification failed');
          if (onFailure) onFailure(e);
        } finally {
          setLoading(false);
        }
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const reason = resp?.error?.description || 'Payment failed';
        toast.error(reason);
        setLoading(false);
        if (onFailure) onFailure(resp);
      });
      rzp.open();
    } catch (e) {
      setLoading(false);
      toast.error('Could not open Razorpay checkout');
      if (onFailure) onFailure(e);
    }
  }, [amount, bookingId, currency, receipt, description, customer, paid, disabled, loading, scriptReady, onSuccess, onFailure, purpose]);

  if (paid) {
    return (
      <Button
        type="button"
        disabled
        className={`bg-green-600 hover:bg-green-600 text-white rounded-none ${className}`}
        data-testid={`${testId}-paid`}
      >
        <CheckCircle size={16} className="mr-1.5" weight="fill" /> Paid
      </Button>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading || !scriptReady}
      className={`bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none ${className}`}
      data-testid={testId}
    >
      {loading ? (
        <>
          <Spinner size={14} className="mr-1.5 animate-spin" /> Opening...
        </>
      ) : (
        <>
          <CurrencyInr size={14} className="mr-1" weight="bold" />
          {label || (amount ? `Pay ₹${Math.round(amount / 100).toLocaleString('en-IN')}` : 'Pay Now')}
        </>
      )}
    </Button>
  );
};

export default RazorpayButton;
